import { Box, Input, VStack } from '@chakra-ui/react';
import { containsFiles, getFiles } from '@atlaskit/pragmatic-drag-and-drop/external/file';
import {
  dropTargetForExternal,
  monitorForExternal,
} from '@atlaskit/pragmatic-drag-and-drop/external/adapter';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CloudUpload } from '@/client/components/icons/individual/CloudUpload';
import DefaultDropzoneContent from './DefaultDropzoneContent';
import { bind } from 'bind-event-listener';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import invariant from 'tiny-invariant';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

// ! This component should be used wherever dropzone functionality is needed for media uploads.

// TODO: Work with design/product to come up with a version(s) of this dropzone upload component for use app wide

// TODO: Generic onDrop handlers: Files, videos, scorm, images

interface MediaUploadDropzoneProps {
  /** Callback function to handle file input */
  onFileInput: any;
  /** Callback function to handle successful drop */
  onSuccessfulDrop: (file: File) => void;
  /** Accepted file types */
  acceptedFileTypes: string[];
  /** Accepted mime types */
  acceptedMimeTypes: string[];
  /** Dropzone content */
  dropzoneContent?: React.ReactNode;
  /** Current media */
  currentMedia?: any;
  /** Allow replace */
  allowReplace?: boolean;
  /** Disable dropzone */
  isDisabled?: boolean;
}

export default function MediaUploadDropzone({
  onFileInput,
  onSuccessfulDrop,
  acceptedFileTypes,
  acceptedMimeTypes,
  dropzoneContent,
  currentMedia,
  allowReplace = false,
  isDisabled = false,
}: MediaUploadDropzoneProps) {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const [dropzoneState, setDropzoneState] = useState<'idle' | 'potential' | 'over'>('idle');

  const dropzoneRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onInputTriggerClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const onFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.currentTarget.files ?? []);

      files.forEach(onFileInput);
    },
    [onFileInput],
  );

  useEffect(() => {
    if (currentMedia || isDisabled) return;

    const el = dropzoneRef.current;

    invariant(el);

    return combine(
      dropTargetForExternal({
        element: el,
        canDrop: (args) => !isDisabled && containsFiles(args),
        onDragEnter: () => !isDisabled && setDropzoneState('over'),
        onDragLeave: () => setDropzoneState('potential'),
        onDrop: async ({ source }) => {
          if (isDisabled) return;

          const files = await getFiles({ source });

          files.forEach((file) => {
            if (file == null) {
              return;
            }

            if (!acceptedMimeTypes.includes(file.type)) {
              setDropzoneState('idle');
              setToast({
                show: true,
                status: 'error',
                title: t('mediaUpload.invalidFileType'),
              });
              return;
            }

            const reader = new FileReader();

            reader.readAsDataURL(file);

            bind(reader, {
              type: 'load',
              listener() {
                const { result } = reader;

                if (typeof result === 'string') {
                  onSuccessfulDrop(file);
                }
              },
            });
          });
        },
      }),
      monitorForExternal({
        canMonitor: (args) => !isDisabled && containsFiles(args),
        onDragStart: () => {
          if (isDisabled) return;

          setDropzoneState('potential');

          preventUnhandled.start();
        },
        onDrop: () => {
          if (isDisabled) return;

          setDropzoneState('idle');

          preventUnhandled.stop();
        },
      }),
    );
  });

  return (
    <Box
      ref={isDisabled ? null : dropzoneRef}
      border="2px dashed"
      borderColor="warmNeutral.300"
      borderRadius="6px"
      paddingX={5}
      paddingY={6}
      backgroundColor={dropzoneState === 'over' ? 'warmNeutral.100' : 'white'}
      _hover={{
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        backgroundColor: isDisabled ? 'white' : 'warmNeutral.100',
      }}
      onClick={isDisabled ? undefined : onInputTriggerClick}
    >
      <Input
        ref={inputRef}
        display="none"
        id="file-input"
        onChange={onFileInputChange}
        type="file"
        accept={acceptedFileTypes.join(',')}
      />

      <VStack>
        <CloudUpload boxSize={10} color="warmNeutral.400" />

        {dropzoneContent || <DefaultDropzoneContent />}
      </VStack>
    </Box>
  );
}
